// Care+ Customers tab — admin view for managing AMC customers + redeeming coupons.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, MessageCircle, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi, errText, fmtDate } from "@/lib/api";
import { WHATSAPP_TEMPLATES, buildWhatsappLink } from "@/lib/whatsappTemplates";

const benefitLabel = (c) => {
  switch (c.benefit) {
    case "FREE": return "FREE";
    case "FLAT_PRICE": return `₹${c.value}`;
    case "PERCENT_OFF": return `${c.value}% off`;
    case "AMOUNT_OFF": return `₹${c.value} off`;
    case "PCT_OFF_LABOR": return `${c.value}% labour`;
    default: return "Coupon";
  }
};

function CustomerWallet({ customerId, onClose, onUpdated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyCoupon, setBusyCoupon] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await adminApi.get(`/admin/customers/${customerId}/wallet`);
      setData(d);
    } catch (e) { toast.error(errText(e)); }
    finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const redeem = async (coupon) => {
    if (!window.confirm(`Mark 1 use of "${coupon.label}" (${coupon.code}) as redeemed?`)) return;
    setBusyCoupon(coupon.id);
    try {
      await adminApi.post(`/admin/coupons/${coupon.id}/redeem`, { note: "" });
      toast.success("Redeemed ✓");
      await load();
      onUpdated?.();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusyCoupon(null); }
  };

  if (loading || !data) return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-8 text-center text-[#71717A]">
      <Loader2 className="w-5 h-5 mx-auto animate-spin" />
    </div>
  );

  const { customer, package: pkg, coupons } = data;
  const active = coupons.filter((c) => c.uses_left > 0);
  const used = coupons.filter((c) => c.uses_left <= 0);

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-sm p-5 sm:p-6" data-testid="customer-wallet">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#D4AF37]">Customer Wallet</p>
          <h3 className="mt-1 text-xl font-semibold text-[#0A0A0A]">{customer.name}</h3>
          <p className="text-xs text-[#71717A]">
            📱 {customer.phone} · joined {fmtDate(customer.created_at)} · last seen {fmtDate(customer.last_login_at)}
          </p>
        </div>
        <Button onClick={onClose} variant="outline" className="h-8 text-xs border-[#E4E4E7]" data-testid="wallet-close">
          <X className="w-3.5 h-3.5 mr-1" /> Close
        </Button>
      </div>

      {/* Cars */}
      <div className="mt-4 flex flex-wrap gap-2">
        {(customer.cars || []).map((c) => (
          <div key={c.id} className="text-xs bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm px-3 py-1.5">
            🚗 <span className="font-semibold text-[#0A0A0A]">{c.brand} {c.model}</span>
            {c.registration && <span className="text-[#71717A] ml-1 font-mono">· {c.registration}</span>}
          </div>
        ))}
        {(!customer.cars || customer.cars.length === 0) && (
          <p className="text-xs text-[#71717A]">No cars registered yet.</p>
        )}
      </div>

      {/* Package status */}
      {pkg ? (
        <div className="mt-4 bg-[#0A0A0A] text-white rounded-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">Care+ Active</p>
            <p className="text-sm mt-0.5">Valid till {fmtDate(pkg.expires_at)} · Order {pkg.razorpay_order_id?.slice(-8)}</p>
          </div>
          <p className="text-2xl font-extrabold text-[#D4AF37]">{active.length}</p>
        </div>
      ) : (
        <p className="mt-4 text-xs text-[#71717A]">No active Care+ Package.</p>
      )}

      {/* Coupons */}
      <div className="mt-5">
        <p className="text-xs uppercase tracking-wider font-bold text-[#52525B] mb-2">
          Active Coupons ({active.length})
        </p>
        <div className="space-y-2">
          {active.length === 0 && <p className="text-xs text-[#71717A]">No active coupons.</p>}
          {active.map((c) => (
            <div key={c.id} className="flex items-center gap-3 bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-3" data-testid={`wallet-coupon-${c.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#0A0A0A] truncate">{c.label}</p>
                <p className="text-[11px] text-[#71717A] font-mono mt-0.5">
                  {c.code} · {benefitLabel(c)} · uses {c.uses_left}/{c.uses_total || c.uses_left} · exp {fmtDate(c.expires_at)}
                </p>
              </div>
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#D4AF37] border border-[#D4AF37]/40 rounded-sm px-2 py-0.5">
                {c.source === "CARE_PLUS" ? "Care+" : c.source}
              </span>
              <Button
                data-testid={`wallet-redeem-${c.id}`}
                onClick={() => redeem(c)}
                disabled={busyCoupon === c.id}
                className="h-8 bg-[#0A0A0A] text-white font-bold text-xs"
              >
                {busyCoupon === c.id ? <Loader2 className="w-3 h-3 animate-spin" /> : (<><Check className="w-3 h-3 mr-1" /> Redeem 1 use</>)}
              </Button>
            </div>
          ))}
        </div>
      </div>

      {used.length > 0 && (
        <div className="mt-6">
          <p className="text-xs uppercase tracking-wider font-bold text-[#52525B] mb-2">
            Fully Used / Expired ({used.length})
          </p>
          <div className="space-y-1">
            {used.map((c) => (
              <p key={c.id} className="text-xs text-[#71717A] line-through">
                {c.label} · {c.code}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [tplPerCustomer, setTplPerCustomer] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = search.trim() ? { search: search.trim() } : {};
      const { data } = await adminApi.get("/admin/customers", { params });
      setCustomers(data);
    } catch (e) { toast.error(errText(e)); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const sendWhatsapp = (c) => {
    const tplId = tplPerCustomer[c.id] || "care_plus_pitch";
    const tpl = WHATSAPP_TEMPLATES.find((t) => t.id === tplId);
    if (!tpl) return;
    // Build a minimal lead-like object so templates can interpolate
    const leadLike = {
      name: c.name, phone: c.phone,
      car: (c.cars && c.cars[0]) ? `${c.cars[0].brand} ${c.cars[0].model}` : "",
    };
    window.open(buildWhatsappLink(c.phone, tpl.build(leadLike)), "_blank", "noopener");
  };

  return (
    <div className="mt-5 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
          <Input
            data-testid="customers-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by phone or name…"
            className="w-72 pl-9 bg-white border-[#E4E4E7] h-9 text-sm"
          />
        </div>
        <span className="text-xs text-[#71717A] ml-auto">{customers.length} customers</span>
      </div>

      {loading && (
        <div className="py-8 text-center text-[#71717A]">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      )}

      {!loading && customers.length === 0 && (
        <div className="bg-white border border-[#E4E4E7] rounded-sm p-10 text-center">
          <p className="text-sm text-[#71717A]">No customers found. Care+ buyers will appear here after purchase.</p>
        </div>
      )}

      <div className="space-y-3" data-testid="customers-list">
        {customers.map((c) => {
          const isOpen = expandedId === c.id;
          const hasPkg = !!c.package;
          return (
            <div key={c.id} className="bg-white border border-[#E4E4E7] rounded-sm" data-testid={`customer-row-${c.phone}`}>
              <div className="p-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                <div className="min-w-[160px]">
                  <p className="font-bold text-sm text-[#0A0A0A]">{c.name}</p>
                  <p className="text-xs text-[#71717A]">{c.phone}</p>
                </div>

                {hasPkg ? (
                  <span className="text-[10px] uppercase tracking-wider font-bold bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40 rounded-sm px-2 py-0.5">
                    Care+ till {fmtDate(c.package.expires_at)}
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider font-bold bg-[#F4F4F5] text-[#71717A] rounded-sm px-2 py-0.5">
                    No Package
                  </span>
                )}

                <span className="text-xs text-[#52525B]">
                  🎫 {c.active_coupons_count} active · {c.redeemed_count || 0} redeemed
                </span>
                <span className="text-xs text-[#71717A]">
                  🚗 {(c.cars || []).length}/2
                </span>

                <div className="ml-auto flex items-center gap-2">
                  <select
                    data-testid={`customer-tpl-${c.phone}`}
                    value={tplPerCustomer[c.id] || "care_plus_pitch"}
                    onChange={(e) => setTplPerCustomer({ ...tplPerCustomer, [c.id]: e.target.value })}
                    className="h-8 text-xs bg-white border border-[#E4E4E7] rounded-sm px-2"
                  >
                    {WHATSAPP_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <Button
                    data-testid={`customer-wa-${c.phone}`}
                    onClick={() => sendWhatsapp(c)}
                    className="h-8 bg-[#25D366] hover:bg-[#1fb457] text-white font-bold text-xs"
                  >
                    <MessageCircle className="w-3 h-3 mr-1" /> Send
                  </Button>
                  <Button
                    data-testid={`customer-view-${c.phone}`}
                    onClick={() => setExpandedId(isOpen ? null : c.id)}
                    variant="outline" className="h-8 text-xs border-[#E4E4E7]"
                  >
                    {isOpen ? "Close" : "View Wallet"}
                  </Button>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-[#E4E4E7] p-4 bg-[#FAFAFA]">
                  <CustomerWallet customerId={c.id} onClose={() => setExpandedId(null)} onUpdated={load} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
